import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";

const Privacy = () => {
  return (
    <Layout>
      {/* GENERATED META — review/refine title + description. */}
      <Seo
        title="Privacy Policy | Blue Heron Marketing"
        description="Learn how Blue Heron Marketing collects, uses, and protects your personal information."
        path="/privacy"
      />
      <div className="py-20">
        <div className="container mx-auto px-6">
          <article className="max-w-4xl mx-auto prose prose-lg">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            
            <p className="text-xl font-semibold text-foreground mb-8">
              Blue Heron
            </p>
            
            <p className="text-muted-foreground mb-8">
              Last Updated: {new Date().toLocaleDateString()}
            </p>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Blue Heron collects information to provide and improve our services. We collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and business information you provide when contacting us or signing up for services</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our website, including IP address, browser type, pages visited, and time spent</li>
                <li><strong>Communication Data:</strong> Records of correspondence and interactions with our team</li>
                <li><strong>Analytics Data:</strong> Website performance metrics and user behavior data</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">2. How We Use Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use collected information for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Providing and maintaining our services</li>
                <li>Communicating with you about services, updates, and marketing</li>
                <li>Improving our website and services</li>
                <li>Analyzing usage patterns and trends</li>
                <li>Preventing fraud and ensuring security</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">3. Information Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell your personal information. We may share information with:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Service Providers:</strong> Third-party vendors who help us operate our business (e.g., hosting, analytics, email services)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All third parties are required to maintain the confidentiality of your information.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">4. Cookies and Tracking</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience. These help us:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Remember your preferences</li>
                <li>Understand how you use our website</li>
                <li>Improve our services</li>
                <li>Deliver relevant content</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Object to certain processing activities</li>
                <li>Data portability</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To exercise these rights, please <Link to="/contact" className="text-primary hover:underline">contact us</Link>.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">7. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy or as required by law. When information is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">8. Third-Party Links</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these sites. We encourage you to review their privacy policies.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">9. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">10. SMS Communications & Consent</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you provide your phone number and opt in to receive SMS messages:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Information Collected:</strong> We collect your phone number and SMS consent preferences</li>
                <li><strong>How Consent is Obtained:</strong> SMS consent is obtained through explicit opt-in checkboxes on our contact forms. Entering a phone number alone does not grant permission to send SMS messages</li>
                <li><strong>Third-Party Sharing:</strong> We do NOT share your SMS consent or phone number with third parties for their marketing purposes. Your information may only be shared with SMS service providers as necessary to deliver messages</li>
                <li><strong>How to Revoke Consent:</strong> You may revoke SMS consent at any time by texting <strong>STOP</strong> to any message you receive, or by <Link to="/contact" className="text-primary hover:underline">contacting us</Link> directly</li>
                <li><strong>Data Retention:</strong> SMS consent records and phone numbers are retained for as long as you maintain consent, plus any period required for legal compliance</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our SMS practices comply with the Telephone Consumer Protection Act (TCPA) and CTIA guidelines.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">11. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have questions about this Privacy Policy or our data practices, please <Link to="/contact" className="text-primary hover:underline">contact us</Link>.
              </p>
            </section>
          </article>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
